/**
 * SafetyReports — operator controls for the scheduled Monthly Safety Summary.
 * Enable/disable auto-delivery, manage INTERNAL recipients, delivery day,
 * reporting period, preview the PDF, send now, and view the audit log.
 * Reporting only — internal recipients only; never auto-sends externally.
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  CalendarClock, Plus, Trash2, Send, Eye, Save, Loader2, ShieldCheck,
  Mail, History, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB") : "—");
const PERIODS = [["previous_month", "Previous month"], ["current_month", "Current month (to date)"]];

export const SafetyReports = () => {
  const [cfg, setCfg] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState("");

  const loadLog = useCallback(async () => {
    try { const { data } = await api.get("/admin/safety/report-log"); setLog(data.rows || []); }
    catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    api.get("/admin/safety/report-config")
      .then(({ data }) => setCfg(data))
      .catch((e) => toast.error(formatApiError(e) || "Failed to load report settings"));
    loadLog();
  }, [loadLog]);

  const upd = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const updRecipient = (i, key, val) => setCfg((c) => {
    const recipients = [...c.recipients];
    recipients[i] = { ...recipients[i], [key]: val };
    return { ...c, recipients };
  });
  const addRecipient = () => setCfg((c) => ({ ...c, recipients: [...(c.recipients || []), { email: "", label: "" }] }));
  const removeRecipient = (i) => setCfg((c) => ({ ...c, recipients: c.recipients.filter((_, j) => j !== i) }));

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put("/admin/safety/report-config", {
        enabled: cfg.enabled,
        recipients: (cfg.recipients || []).filter((r) => r.email && r.email.trim()),
        delivery_day: Number(cfg.delivery_day) || 1,
        reporting_period: cfg.reporting_period,
      });
      setCfg(data);
      toast.success("Report settings saved.");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const preview = () => {
    const url = `${api.defaults.baseURL}/admin/safety/report-config/preview.pdf?period=${cfg.reporting_period}`;
    api.get(`/admin/safety/report-config/preview.pdf?period=${cfg.reporting_period}`, { responseType: "blob" })
      .then(({ data }) => {
        const blobUrl = URL.createObjectURL(data);
        window.open(blobUrl, "_blank");
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      })
      .catch((e) => toast.error(formatApiError(e) || "Preview failed"));
    return url;
  };

  const sendNow = async () => {
    const recips = (cfg.recipients || []).map((r) => r.email).filter(Boolean);
    if (!window.confirm(`Send the ${cfg.reporting_period === "previous_month" ? "previous" : "current"}-month Safety Summary now to: ${recips.join(", ") || "info@crystalworkforce.com"}?`)) return;
    setBusy("send");
    try {
      const { data } = await api.post("/admin/safety/report-config/send-now", { period: cfg.reporting_period });
      data.ok ? toast.success(`Summary sent to ${data.recipients.length} recipient(s).`)
        : toast.warning("Summary attempted — check the delivery log for failures.");
      loadLog();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  if (!cfg) return (
    <div className="p-8 text-sm text-muted-foreground flex items-center gap-2" data-testid="safety-reports-loading">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading report settings…
    </div>
  );

  return (
    <div className="space-y-4" data-testid="safety-reports">
      {/* Enable + schedule */}
      <div className="border border-border rounded-xl p-4 bg-white space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-sm font-bold flex items-center gap-1.5"><CalendarClock className="w-4 h-4" /> Automatic monthly delivery</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Generates the Monthly Safety Summary PDF and emails it to your internal recipients.</p>
          </div>
          <button onClick={() => upd({ enabled: !cfg.enabled })} data-testid="reports-enabled-toggle"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${cfg.enabled ? "bg-[#065F46]" : "bg-gray-300"}`}>
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${cfg.enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Reporting period</label>
            <select value={cfg.reporting_period} onChange={(e) => upd({ reporting_period: e.target.value })} data-testid="reports-period"
              className="mt-1 w-full border border-border rounded-lg px-2.5 py-1.5 text-sm bg-white">
              {PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Delivery day of month (1–28)</label>
            <input type="number" min={1} max={28} value={cfg.delivery_day} onChange={(e) => upd({ delivery_day: e.target.value })} data-testid="reports-delivery-day"
              className="mt-1 w-full border border-border rounded-lg px-2.5 py-1.5 text-sm" />
          </div>
        </div>

        {/* Recipients */}
        <div>
          <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Internal recipients</label>
          <div className="mt-1.5 space-y-2">
            {(cfg.recipients || []).map((r, i) => (
              <div key={i} className="flex gap-2" data-testid={`reports-recipient-${i}`}>
                <input value={r.email} onChange={(e) => updRecipient(i, "email", e.target.value)} placeholder="email@company.com" data-testid={`reports-recipient-email-${i}`}
                  className="flex-1 border border-border rounded-lg px-2.5 py-1.5 text-sm" />
                <input value={r.label || ""} onChange={(e) => updRecipient(i, "label", e.target.value)} placeholder="Role (e.g. Compliance Manager)" data-testid={`reports-recipient-label-${i}`}
                  className="flex-1 border border-border rounded-lg px-2.5 py-1.5 text-sm" />
                <button onClick={() => removeRecipient(i)} data-testid={`reports-recipient-remove-${i}`}
                  className="p-2 border border-border rounded-lg hover:bg-secondary text-[#DC2626]"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={addRecipient} data-testid="reports-add-recipient"
              className="text-xs font-semibold border border-border rounded-lg px-3 py-1.5 flex items-center gap-1.5 hover:bg-secondary"><Plus className="w-3.5 h-3.5" /> Add recipient</button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-1 border-t border-border mt-1">
          <button onClick={save} disabled={busy === "save"} data-testid="reports-save"
            className="mt-3 text-xs font-semibold bg-[#0A0A0A] text-white rounded-lg px-3 py-2 flex items-center gap-1.5 disabled:opacity-50">
            {busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save settings</button>
          <button onClick={preview} data-testid="reports-preview"
            className="mt-3 text-xs font-semibold border border-border rounded-lg px-3 py-2 flex items-center gap-1.5 hover:bg-secondary"><Eye className="w-3.5 h-3.5" /> Preview PDF</button>
          <button onClick={sendNow} disabled={busy === "send"} data-testid="reports-send-now"
            className="mt-3 text-xs font-semibold bg-[#065F46] hover:bg-[#064E3B] text-white rounded-lg px-3 py-2 flex items-center gap-1.5 disabled:opacity-50">
            {busy === "send" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send now</button>
          {cfg.last_sent_at && <span className="mt-3 text-[11px] text-muted-foreground self-center">Last auto-sent: {fmt(cfg.last_sent_at)}</span>}
        </div>
      </div>

      {/* Security note */}
      <div className="text-[11px] bg-[#FFFBEB] text-[#92400E] border border-[#FCD34D] rounded-lg px-3 py-2 flex items-start gap-1.5" data-testid="reports-security-note">
        <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
        <span>Reports are sent to <b>internal recipients only</b>. Crystal Dispatch never auto-sends to councils, insurers or external organisations — share externally manually if required. Every generated and emailed report is recorded in the delivery log below.</span>
      </div>

      {/* Delivery log */}
      <div>
        <h4 className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5 mb-2"><History className="w-3.5 h-3.5" /> Delivery log</h4>
        {log.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground border border-dashed border-border rounded-xl" data-testid="reports-log-empty">No reports generated yet.</div>
        ) : (
          <div className="border border-border rounded-xl overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-[#0A0A0A] text-white"><tr className="text-left">
                <th className="px-3 py-2 font-semibold">Generated</th>
                <th className="px-3 py-2 font-semibold">Period</th>
                <th className="px-3 py-2 font-semibold">Recipients</th>
                <th className="px-3 py-2 font-semibold">Incidents</th>
                <th className="px-3 py-2 font-semibold">Trigger</th>
                <th className="px-3 py-2 font-semibold">Result</th>
              </tr></thead>
              <tbody>
                {log.map((r, i) => {
                  const allSent = (r.statuses || []).every((s) => ["sent", "skipped_no_key"].includes(s.status));
                  return (
                    <tr key={r.id} data-testid={`reports-log-row-${r.id}`} className={`border-t border-border ${i % 2 ? "bg-[#FAFAFA]" : "bg-white"}`}>
                      <td className="px-3 py-2 whitespace-nowrap">{fmt(r.generated_at)}</td>
                      <td className="px-3 py-2">{r.period_label}</td>
                      <td className="px-3 py-2 text-muted-foreground">{(r.recipients || []).join(", ")}</td>
                      <td className="px-3 py-2">{r.incident_count}</td>
                      <td className="px-3 py-2 text-muted-foreground">{r.triggered_by}</td>
                      <td className="px-3 py-2">
                        {allSent
                          ? <span className="inline-flex items-center gap-1 text-[#065F46]"><CheckCircle2 className="w-3.5 h-3.5" /> Sent</span>
                          : <span className="inline-flex items-center gap-1 text-[#B45309]"><AlertTriangle className="w-3.5 h-3.5" /> Partial</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SafetyReports;
